import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Calculator, Search, Plus, Pencil, Archive, Copy, Wand2, ChevronRight, ChevronDown, Users, Download, CalendarDays, ArchiveRestore, Share2, Trophy, FileEdit } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COT_STATUS_LABELS, COT_STATUS_VARIANT, COT_STATUS_GROUPS, normalizeCotStatus, PARTNER_LABELS, fmtEUR, fmtDate, fmtRangeLong } from "@/pages/cotizador/shared";
import CotizadorDetail from "@/pages/cotizador/CotizadorDetail";
import { CotTravefyImport } from "@/pages/cotizador/CotTravefyImport";
import ContactSearchInput from "@/components/ContactSearchInput";

const EMPTY = { name: "", itinerary_title: "", client_email: "", destination: "", partner: "other", num_travelers: 1, fx_rate: "" };
const MANAGER_ROLES = new Set(["director_sales", "dir_operaciones", "socio_director", "dir_admin_general"]);

export default function CotizadorPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.has(user?.role);
  const [sp, setSp] = useSearchParams();
  const [selected, setSelectedState] = useState(() => sp.get("id") || null);
  const setSelected = (id) => { setSelectedState(id); setSp(id ? { id } : {}, { replace: true }); };
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("activos");
  // Managers (dirección/jefe de ventas) ven todas las cotizaciones. Por defecto arrancan
  // mostrando solo las suyas ("mine"), para no ruido; pueden abrir "Todos" desde el filtro.
  // Los agentes normales ya están limitados a las suyas + compartidas por el backend.
  const [agent, setAgent] = useState("mine");
  const [open, setOpen] = useState(false);
  const [travefyOpen, setTravefyOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleGroup = (gid) => setExpanded((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });
  const baseName = (n) => (n || "").replace(/\s*[·-]\s*v\d+\s*$/i, "");
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQ(term), 350); return () => clearTimeout(t); }, [term]);

  const partnersQ = useQuery({ queryKey: ["formula-partners"], queryFn: async () => (await api.get("/formulas/partners")).data });
  const partners = partnersQ.data?.partners || [];
  const agentsQ = useQuery({
    queryKey: ["cot-agents"],
    queryFn: async () => (await api.get("/cotizaciones/agents")).data,
    enabled: isManager,
  });
  const agents = agentsQ.data?.items || [];
  const listQ = useQuery({
    queryKey: ["cotizaciones", q, status, isManager ? agent : "self", user?.email],
    queryFn: async () => {
      // Filtro por agente: "mine" = solo las del usuario actual · "all" = todas (managers) ·
      // email concreto = ese agente. Se resuelve en el cliente antes de llamar al API.
      const agentParam = !isManager ? "all" : (agent === "mine" ? (user?.email || "all") : agent);
      return (await api.get("/cotizaciones", { params: { q, status_f: status === "all" ? "" : status, agent: agentParam } })).data;
    },
  });
  const items = useMemo(() => listQ.data?.items || [], [listQ.data]);
  const groups = useMemo(() => {
    const map = new Map();
    for (const c of items) {
      const gid = c.version_group_id || c.id;
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push(c);
    }
    const arr = [];
    for (const [gid, versions] of map) {
      versions.sort((a, b) => (b.version || 1) - (a.version || 1));
      // Al buscar el "representante" del grupo, priorizamos la versión vendida
      // (nueva clave canónica). Mantenemos `ganado` en el chequeo por si aún
      // queda algún doc legacy en DB sin migrar.
      const rep = versions.find((v) => v.status === "vendido" || v.status === "ganado") || versions[0];
      arr.push({ gid, versions, rep });
    }
    arr.sort((a, b) => (b.rep.updated_at || "").localeCompare(a.rep.updated_at || ""));
    return arr;
  }, [items]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const saveMut = useMutation({
    mutationFn: () => api.post("/cotizaciones", {
      name: form.name, itinerary_title: form.itinerary_title || "",
      main_traveler: form.name, client_email: form.client_email || "",
      destination: form.destination, partner: form.partner,
      num_travelers: parseInt(form.num_travelers) || 1, adults: parseInt(form.num_travelers) || 1,
      fx_rate: form.fx_rate ? parseFloat(form.fx_rate) : null,
    }),
    onSuccess: (res) => { toast.success("Cotización creada"); qc.invalidateQueries({ queryKey: ["cotizaciones"] }); setOpen(false); setSelected(res.data.id); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const dupMut = useMutation({
    mutationFn: (id) => api.post(`/cotizaciones/${id}/duplicate`),
    onSuccess: (res) => { toast.success(`Versión v${res.data.version} creada`); qc.invalidateQueries({ queryKey: ["cotizaciones"] }); setExpanded((s) => new Set(s).add(res.data.version_group_id)); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const archiveMut = useMutation({
    mutationFn: (id) => api.post(`/cotizaciones/${id}/archive`),
    onSuccess: () => { toast.success("Cotización archivada — no vendida"); qc.invalidateQueries({ queryKey: ["cotizaciones"] }); setDelTarget(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  // Cambio manual de estado desde la lista (menú en el badge). Permite mover entre estados
  // y RESCATAR de archivado con un click. Usa el PUT genérico — el backend normaliza.
  const statusMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/cotizaciones/${id}`, { status })).data,
    onSuccess: (_res, vars) => {
      toast.success(`Movido a "${COT_STATUS_LABELS[vars.status] || vars.status}"`);
      qc.invalidateQueries({ queryKey: ["cotizaciones"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (selected) {
    return <CotizadorDetail key={selected} cotId={selected} onBack={() => { setSelected(null); qc.invalidateQueries({ queryKey: ["cotizaciones"] }); }} onOpen={(id) => setSelected(id)} />;
  }

  return (
    <div className="space-y-5" data-testid="cotizador-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <Calculator className="h-6 w-6 text-primary" /> Creador de Itinerarios
          </h1>
          <p className="text-sm text-muted-foreground">Constructor de cotizaciones día a día. Todo viaje termina aquí antes de pasar a Trip.</p>
        </div>
        <Button size="lg" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-cot-button" className="shadow-sm">
          <Plus className="mr-1.5 h-4 w-4" />Nueva cotización
        </Button>
      </div>

      {/* Franja de importadores — Travefy solo. KimKim se importa directo a Trip (Trips → "Import from KimKim"). */}
      <div className="flex flex-col gap-3 rounded-lg border border-dashed border-primary/30 bg-primary/[0.02] p-3 sm:flex-row sm:items-center sm:justify-between" data-testid="external-importers-strip">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Download className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold">¿Tienes un itinerario Travefy que quieres presupuestar?</p>
            <p className="text-xs text-muted-foreground">Impórtalo aquí para crear la cotización. Los viajes ya vendidos por KimKim se importan directamente desde <strong>Trips → Import from KimKim</strong>.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="default" onClick={() => setTravefyOpen(true)} data-testid="travefy-import-button">
            <Wand2 className="mr-1.5 h-4 w-4" />Import from Travefy
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative sm:max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar por nombre, viajero, destino…" className="pl-8" data-testid="cot-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-64" data-testid="cot-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="activos" data-testid="cot-status-activos">{COT_STATUS_GROUPS.activos.label}</SelectItem>
            <SelectItem value="borrador">{COT_STATUS_LABELS.borrador}</SelectItem>
            <SelectItem value="compartido">{COT_STATUS_LABELS.compartido}</SelectItem>
            <SelectSeparator />
            <SelectItem value="no_activos" data-testid="cot-status-no-activos">{COT_STATUS_GROUPS.no_activos.label}</SelectItem>
            <SelectItem value="vendido">{COT_STATUS_LABELS.vendido}</SelectItem>
            <SelectItem value="archivado">{COT_STATUS_LABELS.archivado}</SelectItem>
            <SelectSeparator />
            <SelectItem value="all">Todos los estados</SelectItem>
          </SelectContent>
        </Select>
        {isManager && (
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="sm:w-52" data-testid="cot-agent-filter"><SelectValue placeholder="Agente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mine" data-testid="cot-agent-mine">Solo las mías</SelectItem>
              <SelectItem value="all">Todos los agentes</SelectItem>
              <SelectSeparator />
              {agents.map((a) => <SelectItem key={a.email} value={a.email}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center" data-testid="cot-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Calculator className="h-6 w-6" /></div>
              <p className="font-semibold">Sin cotizaciones</p>
              <p className="text-sm text-muted-foreground">Crea una cotización para empezar a montar el itinerario.</p>
            </div>
          ) : (
            <>
            {/* Vista MÓVIL (< md): cards compactas con toda la info clave visible sin scroll horizontal. */}
            <div className="divide-y md:hidden">
              {groups.map((g) => {
                const c = g.rep;
                const st = normalizeCotStatus(c.status);
                return (
                  <div
                    key={g.gid}
                    onClick={() => setSelected(c.id)}
                    className="active:bg-muted/60 flex flex-col gap-1.5 p-3"
                    data-testid={`cot-row-mobile-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Badge variant={COT_STATUS_VARIANT[st] || "secondary"} className="text-[9px] uppercase tracking-wide font-semibold">
                            {COT_STATUS_LABELS[st] || "Borrador"}
                          </Badge>
                          {c.version && c.version > 1 ? (
                            <Badge variant="outline" className="border-primary/40 bg-primary/5 text-[9px] font-bold tabular-nums text-primary">v{c.version}</Badge>
                          ) : null}
                          <Badge variant="outline" className="text-[9px] font-normal">{PARTNER_LABELS[c.partner] || PARTNER_LABELS.other}</Badge>
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm font-medium">{c.itinerary_title || baseName(c.name) || c.name}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular-nums">{fmtEUR(c.result?.totals?.pvp_eur || 0)}</p>
                        <p className="text-[10px] text-muted-foreground">final</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className="truncate"><span className="opacity-70">Cliente:</span> {c.main_traveler || "—"}</span>
                      <span className="truncate"><span className="opacity-70">Agente:</span> {c.consultant?.name || c.agent || "—"}</span>
                      <span className="truncate flex items-center gap-1"><CalendarDays className="h-3 w-3 opacity-50" />{fmtRangeLong(c.start_date, c.end_date)}</span>
                      <span className="truncate flex items-center gap-1"><Users className="h-3 w-3 opacity-50" />{c.num_travelers || "—"} pax</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Vista DESKTOP (>= md): tabla completa. */}
            <div className="hidden overflow-x-auto md:block">
              <Table data-testid="cot-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Fuente</TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Fechas</TableHead>
                    <TableHead className="text-right">Pax</TableHead>
                    <TableHead className="text-right">Precio final</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => {
                    const c = g.rep;
                    const multi = g.versions.length > 1;
                    const isExp = expanded.has(g.gid);
                    return (
                      <React.Fragment key={g.gid}>
                        <TableRow className="cursor-pointer" onClick={() => setSelected(c.id)} data-testid={`cot-row-${c.id}`}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-1.5">
                              {multi ? (
                                <button onClick={(e) => { e.stopPropagation(); toggleGroup(g.gid); }} className="rounded p-0.5 hover:bg-muted" data-testid={`cot-group-toggle-${g.gid}`} aria-label="Ver versiones">
                                  {isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                              ) : <span className="inline-block w-5" />}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <button data-testid={`cot-status-menu-${c.id}`} className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm">
                                    <Badge variant={COT_STATUS_VARIANT[normalizeCotStatus(c.status)] || "secondary"} className="cursor-pointer uppercase tracking-wide text-[10px] font-semibold hover:opacity-80" data-testid={`cot-status-badge-${c.id}`}>
                                      {COT_STATUS_LABELS[normalizeCotStatus(c.status)] || "Borrador"}
                                      <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                                    </Badge>
                                  </button>
                                </DropdownMenuTrigger>
                                <StatusMenuContent id={c.id} current={normalizeCotStatus(c.status)} onPick={(st) => statusMut.mutate({ id: c.id, status: st })} />
                              </DropdownMenu>
                              {(c.version && c.version > 1) ? (
                                <Badge variant="outline" className="border-primary/40 bg-primary/5 uppercase text-[10px] font-bold tabular-nums text-primary" data-testid={`cot-version-badge-${c.id}`}>v{c.version}</Badge>
                              ) : null}
                              <span className="truncate max-w-[280px]">{c.itinerary_title || baseName(c.name) || c.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <Badge variant="outline" className="text-[11px] font-normal" data-testid={`cot-source-badge-${c.id}`}>
                              {PARTNER_LABELS[c.partner] || PARTNER_LABELS.other}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{c.consultant?.name || c.agent || c.created_by || "—"}</TableCell>
                          <TableCell className="text-sm">{c.main_traveler || "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <CalendarDays className="h-3 w-3 opacity-60" />
                              {fmtRangeLong(c.start_date, c.end_date)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums">
                            <span className="inline-flex items-center gap-1 justify-end">
                              <Users className="h-3 w-3 opacity-60" />
                              {c.num_travelers || "—"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums">{fmtEUR(c.result?.totals?.pvp_eur || 0)}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setSelected(c.id)} data-testid={`view-cot-${c.id}`} aria-label="Abrir" title="Abrir"><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dupMut.mutate(c.id)} data-testid={`dup-cot-${c.id}`} aria-label="Nueva versión" title="Crear nueva versión"><Copy className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => setDelTarget(c)} data-testid={`del-cot-${c.id}`} aria-label="Archivar" title="Archivar — no vendida"><Archive className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                        {isExp && g.versions.map((v) => (
                          <TableRow key={v.id} className="cursor-pointer bg-muted/30" onClick={() => setSelected(v.id)} data-testid={`cot-version-row-${v.id}`}>
                            <TableCell className="pl-10 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1.5">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <button data-testid={`cot-status-menu-${v.id}`} className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm">
                                      <Badge variant={COT_STATUS_VARIANT[normalizeCotStatus(v.status)] || "secondary"} className="cursor-pointer uppercase tracking-wide text-[10px] font-semibold hover:opacity-80">
                                        {COT_STATUS_LABELS[normalizeCotStatus(v.status)] || "Borrador"}
                                        <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                                      </Badge>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <StatusMenuContent id={v.id} current={normalizeCotStatus(v.status)} onPick={(st) => statusMut.mutate({ id: v.id, status: st })} />
                                </DropdownMenu>
                                <Badge variant="outline" className="border-primary/40 bg-primary/5 uppercase text-[10px] font-bold tabular-nums text-primary">v{v.version || 1}</Badge>
                                <span className="truncate max-w-[240px]">{v.itinerary_title || v.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              <Badge variant="outline" className="text-[11px] font-normal">
                                {PARTNER_LABELS[v.partner] || PARTNER_LABELS.other}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{v.consultant?.name || v.agent || v.created_by || "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{v.main_traveler || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                              <span className="inline-flex items-center gap-1">
                                <CalendarDays className="h-3 w-3 opacity-60" />
                                {fmtRangeLong(v.start_date, v.end_date)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm tabular-nums">
                              <span className="inline-flex items-center gap-1 justify-end">
                                <Users className="h-3 w-3 opacity-60" />
                                {v.num_travelers || "—"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{fmtEUR(v.result?.totals?.pvp_eur || 0)}</TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setSelected(v.id)} data-testid={`view-cot-${v.id}`} aria-label="Abrir"><Pencil className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dupMut.mutate(v.id)} data-testid={`dup-cot-${v.id}`} aria-label="Nueva versión"><Copy className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => setDelTarget(v)} data-testid={`del-cot-${v.id}`} aria-label="Archivar" title="Archivar — no vendida"><Archive className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </CardContent>
      </Card>
      <div className="text-sm text-muted-foreground" data-testid="cot-count">{groups.length} clientes · {items.length} {items.length === 1 ? "versión" : "versiones"}</div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cot-dialog">
          <DialogHeader>
            <DialogTitle>Nueva cotización</DialogTitle>
            <DialogDescription>Datos básicos. Luego añade los días y servicios del itinerario.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Título del itinerario *</Label>
              <Input value={form.itinerary_title} onChange={(e) => set("itinerary_title", e.target.value)} placeholder="Ej.: A Portuguese Getaway: Best of Lisbon and Porto" data-testid="cot-title-input" />
              <p className="text-[11px] text-muted-foreground">Este es el nombre que verá el cliente en su itinerario.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Viajero principal / Cliente *</Label>
              <ContactSearchInput
                value={form.name}
                onChange={(v) => set("name", v)}
                onSelect={(c) => setForm((s) => ({ ...s, name: c.name, client_email: c.email || "", destination: s.destination || c.destination || "" }))}
                placeholder="Busca un cliente existente o escribe uno nuevo…"
                testid="cot-name-input"
              />
              <p className="text-[11px] text-muted-foreground">Si el cliente ya está en el sistema, elígelo de la lista; si no, escribe su nombre y se creará nuevo.</p>
            </div>
            <div className="space-y-1.5"><Label>Destino</Label><Input value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="Peru, Spain…" data-testid="cot-destination-input" /></div>
            <div className="space-y-1.5">
              <Label>Fuente / Partner</Label>
              <Select value={form.partner} onValueChange={(v) => set("partner", v)}>
                <SelectTrigger data-testid="cot-partner-input"><SelectValue /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Pax (viajeros)</Label><Input type="number" min="1" value={form.num_travelers} onChange={(e) => set("num_travelers", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cambio €/$ (opcional)</Label><Input type="number" step="0.0001" value={form.fx_rate} onChange={(e) => set("fx_rate", e.target.value)} placeholder="1.08" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || !form.itinerary_title || saveMut.isPending} data-testid="save-cot-button">{saveMut.isPending ? "Creando…" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="del-cot-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar cotización?</AlertDialogTitle>
            <AlertDialogDescription>«{delTarget?.name}» pasará a <b>Archivado — No vendido</b>. Podrás recuperarla filtrando por ese estado.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveMut.mutate(delTarget.id)} data-testid="confirm-del-cot">Archivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CotTravefyImport
        open={travefyOpen}
        onClose={() => setTravefyOpen(false)}
        onCreated={(id) => { setTravefyOpen(false); qc.invalidateQueries({ queryKey: ["cotizaciones"] }); setSelected(id); }}
      />
    </div>
  );
}


/** Menú desplegable del badge de estado. Cambia el status de una cotización con un click.
 *  Diferencia clave para "archivado" (No vendido): muestra "Restaurar a Borrador" arriba y
 *  destacado, para que el rescate sea el primer verbo natural del menú.
 *  El item del status actual queda desactivado y marcado como "(actual)". */
function StatusMenuContent({ id, current, onPick }) {
  const items = [
    { key: "borrador", label: "Borrador", icon: FileEdit },
    { key: "compartido", label: "Compartido", icon: Share2 },
    { key: "vendido", label: "Vendido", icon: Trophy, className: "text-emerald-700" },
    { key: "archivado", label: "Archivar — No vendido", icon: Archive, className: "text-muted-foreground" },
  ];
  const isArchived = current === "archivado";
  return (
    <DropdownMenuContent align="start" className="min-w-[220px]" data-testid={`cot-status-menu-content-${id}`}>
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Cambiar estado</DropdownMenuLabel>
      {isArchived && (
        <>
          <DropdownMenuItem onClick={() => onPick("borrador")} className="text-primary font-medium" data-testid={`cot-status-restore-${id}`}>
            <ArchiveRestore className="mr-2 h-3.5 w-3.5" />Restaurar a Borrador
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      {items.map(({ key, label, icon: Icon, className = "" }) => {
        const isCurrent = key === current;
        return (
          <DropdownMenuItem
            key={key}
            disabled={isCurrent}
            className={className}
            onClick={() => !isCurrent && onPick(key)}
            data-testid={`cot-status-set-${key}-${id}`}
          >
            <Icon className="mr-2 h-3.5 w-3.5" />
            {label}
            {isCurrent && <span className="ml-auto text-[10px] text-muted-foreground">(actual)</span>}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );
}
